import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { motion } from 'framer-motion';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';

import { Link } from 'gatsby';
import InstaFeed from '../services/InstaFeed';

export default function FreeZoneInstaFeed({ data, id }) {
  const { instaTitle } = data;
  const [posts, setPosts] = useState([]);
  const feedId = '6f6e3d75-ebda-4c02-8fea-220ee3b969ba';
  
  const FETCH_INTERVAL = 86400000; // 1440 minutes = 1 day interval for fetching data

  const fetchInstagramFeed = () => {
    // Create a new instance of InstaFeed with the designated feed ID
    const feed = new InstaFeed(feedId);
  
    // Fetch posts using the InstaFeed instance
    feed
      .get()
      .then((res) => {
        // On successful response, update the 'posts' state with the fetched data
        setPosts(res.posts);
      })
      .catch((err) => {
        // Log any errors that occur during the API call for debugging
        console.error('Failed to fetch Instagram feed:', err);
      });
  };

  useEffect(() => {
    // Initial fetch
    fetchInstagramFeed();

    // Set up interval to fetch feed periodically
    const intervalId = setInterval(fetchInstagramFeed, FETCH_INTERVAL);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  return (
    <section id={id} 
      className="bg-[#ebebeb] instagram-feed py-[3rem] md:py-[5.5rem] xl:py-[6.5rem]">
      <div
        className="flex flex-col gap-10 lg:gap-12 z-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          viewport={{ once: true }}
          className="flex flex-col gap-y-7 lg:gap-y-[30px] px-6 lg:mx-8">
          <div className="flex flex-col gap-y-7 md:gap-y-5">
            <div
              className="text-clamp20to40 h2-child:text-clamp20to40 text-[#231F20] h2-child:leading-1.2 text-center h2-child:!m-0 md:!text-center ltr:text-left rtl:text-right">
              <h2 dangerouslySetInnerHTML={{ __html: instaTitle }} />
            </div>
            <div className="h-[2px] w-[70px] bg-[#CE445B] md:mx-auto" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className='overflow-hidden'
          >
          <Swiper
            navigation
            spaceBetween={32}
            slidesPerView="auto"
            modules={[Autoplay, Navigation]}
            className="image_card">
            {!isEmpty(posts) &&
              posts.map((item, index) => (
                <SwiperSlide
                  key={index}
                  className="overflow-hidden h-full sm:max-w-[280px] md:max-w-[320px] lg:max-w-[370px]">
                  <Link
                    href={item.url}
                    rel="noreferrer"
                    target="_blank"
                    aria-label="instagram images"
                    className="relative block w-full [&>span]:pt-[100%] [&>span]:w-full [&>span]:overflow-hidden [&>span]:relative">
                    <svg
                      width="25"
                      height="25"
                      viewBox="0 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="absolute right-5 bottom-5 z-10">
                      <path
                        d="M12.5028 6.09032C8.95566 6.09032 6.09453 8.95208 6.09453 12.5C6.09453 16.0479 8.95566 18.9097 12.5028 18.9097C16.0499 18.9097 18.911 16.0479 18.911 12.5C18.911 8.95208 16.0499 6.09032 12.5028 6.09032ZM12.5028 16.6671C10.2105 16.6671 8.33659 14.7983 8.33659 12.5C8.33659 10.2017 10.205 8.33287 12.5028 8.33287C14.8006 8.33287 16.669 10.2017 16.669 12.5C16.669 14.7983 14.795 16.6671 12.5028 16.6671ZM20.6679 5.82813C20.6679 6.65932 19.9986 7.32316 19.1732 7.32316C18.3422 7.32316 17.6785 6.65374 17.6785 5.82813C17.6785 5.00251 18.3477 4.33309 19.1732 4.33309C19.9986 4.33309 20.6679 5.00251 20.6679 5.82813ZM24.9122 7.34548C24.8173 5.3428 24.36 3.56884 22.8932 2.10727C21.432 0.64571 19.6584 0.188274 17.6562 0.0878612C15.5926 -0.0292871 9.40742 -0.0292871 7.34384 0.0878612C5.34718 0.182696 3.57362 0.640132 2.1068 2.1017C0.639989 3.56326 0.188232 5.33722 0.0878416 7.3399C-0.0292805 9.40394 -0.0292805 15.5905 0.0878416 17.6545C0.182655 19.6572 0.639989 21.4312 2.1068 22.8927C3.57362 24.3543 5.34161 24.8117 7.34384 24.9121C9.40742 25.0293 15.5926 25.0293 17.6562 24.9121C19.6584 24.8173 21.432 24.3599 22.8932 22.8927C24.3544 21.4312 24.8118 19.6572 24.9122 17.6545C25.0293 15.5905 25.0293 9.40952 24.9122 7.34548ZM22.2462 19.8692C21.8112 20.9626 20.969 21.8049 19.8703 22.2456C18.225 22.8983 14.321 22.7477 12.5028 22.7477C10.6846 22.7477 6.77496 22.8927 5.13525 22.2456C4.04211 21.8105 3.19994 20.9681 2.75934 19.8692C2.1068 18.2235 2.25739 14.3186 2.25739 12.5C2.25739 10.6814 2.11238 6.77089 2.75934 5.13082C3.19437 4.03743 4.03653 3.19508 5.13525 2.75438C6.78054 2.1017 10.6846 2.25232 12.5028 2.25232C14.321 2.25232 18.2306 2.10727 19.8703 2.75438C20.9635 3.1895 21.8056 4.03185 22.2462 5.13082C22.8988 6.77647 22.7482 10.6814 22.7482 12.5C22.7482 14.3186 22.8988 18.2291 22.2462 19.8692Z"
                        fill="white"
                      />
                    </svg>
                    <LazyLoadImage
                      src={item.image_large}
                      alt="instagram images"
                      effect="blur"
                      placeholderSrc={item ? `${item.image_large}?width=50&quality=50` : null}
                      className="object-cover w-full h-full absolute z-0 left-0 right-0 top-0 inst-img"
                    />
                  </Link>
                </SwiperSlide>
              ))}
          </Swiper>
        </motion.div>
      </div>
    </section>
  );
}